/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useQueueList.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Queues list state and logic.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchQueues } from '../api/queuesApi';
import { ListQueuesParams, QueueResponse } from '../types';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';

interface UseQueueListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for managing Queues list
 * Implements Domain Contextual Filtering pattern
 */
export function useQueueList(options: UseQueueListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      const queryParams: ListQueuesParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        domainId: effectiveDomainId
      };

      const result = await fetchQueues(queryParams);

      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('queues.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, effectiveDomainId]);

  const availableColumns = useMemo(() => [
    { key: 'name', label: t('queues.columns.name'), visible: true, sortable: true },
    { key: 'extension', label: t('queues.columns.extension'), visible: true, sortable: true },
    { key: 'strategy', label: t('queues.columns.strategy'), visible: true, sortable: true },
    { key: 'maxWaitTime', label: t('queues.columns.maxWaitTime'), visible: true, sortable: true },
    { key: 'enabled', label: t('queues.columns.enabled'), visible: true, sortable: true },
    { key: 'updatedAt', label: t('queues.columns.updatedAt'), visible: true, sortable: true },
    { key: 'actions', label: t('common.actions'), visible: true, sortable: false }
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'queues',
    reloadKey: effectiveDomainId ?? null
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as QueueResponse[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId
  };
}

